import { EMPTY_STRING } from '../constants/common'
import {
  COMPANY_SETTINGS_SECTION_HASH_PREFIX,
  COMPANY_SETTINGS_SECTION_TAB_IDS,
} from '../constants/companySettings'
import {
  HTML_LINK_PROTOCOL_MAILTO,
  HTML_LINK_PROTOCOL_TEL,
} from '../constants/html'
import { LINKS } from '../constants/link'
import {
  PROFILE_ROUTE_NAMES,
  ROUTE_PATH_LIST,
  ROUTE_PATH_LIST_SETTINGS,
  ROUTE_UI_SECTION_NAME,
  SUBMENU_PARAM_KEY,
  SUBMENU_PARAM_KEY_OLD,
  TAB_PARAM_KEY,
} from '../constants/route'

import { getWebappLinkPrefix } from './route'

export function isNativeLink(to, appAvailable) {
  if (!appAvailable) {
    return false
  }

  return LINKS.includes(to)
}

export function getPath(to, appAvailable) {
  if (to === '/' && !appAvailable) {
    return getWebappLinkPrefix()
  }

  if (!appAvailable || isNativeLink(to, appAvailable)) {
    return getWebappLinkPrefix(to)
  }

  return to
}

export function getRedirectPathname(pathname, search) {
  const redirectPathname =
    pathname !== '/' && pathname !== '/login' ? pathname : EMPTY_STRING

  if (redirectPathname || search) {
    return `?redirect=${pathname}${search}`
  }

  return EMPTY_STRING
}

export function getNotificationViewTableRedirectPath(pathname, sub) {
  const queryParam = sub ? `?${SUBMENU_PARAM_KEY}=${sub}` : EMPTY_STRING

  return `${pathname}${queryParam}`
}

export function getEmailProfilePathname({
  availableSections,
  id,
  singleEmailAccount,
  sub,
}) {
  const isProfileAvailable = availableSections[ROUTE_UI_SECTION_NAME.mailThread]

  const additionalParams = {
    additionalParams: sub
      ? `?${isProfileAvailable ? SUBMENU_PARAM_KEY : SUBMENU_PARAM_KEY_OLD}=${sub}`
      : undefined,
    id,
  }

  const routeName = isProfileAvailable
    ? singleEmailAccount
      ? PROFILE_ROUTE_NAMES.emailMessage
      : PROFILE_ROUTE_NAMES.emailThreadNG
    : singleEmailAccount
      ? PROFILE_ROUTE_NAMES.email
      : PROFILE_ROUTE_NAMES.emailThread

  return routeName(additionalParams)
}

export function getRequestProfilePathname({
  additionalParams,
  availableSections,
  id,
}) {
  const routeName = availableSections[ROUTE_UI_SECTION_NAME.serviceRequest]
    ? PROFILE_ROUTE_NAMES.requestProfileNG
    : PROFILE_ROUTE_NAMES.request

  return routeName({ additionalParams, id })
}

export function getBudgetPlanProfilePathname({
  availableSections,
  id,
  requestId,
}) {
  if (availableSections[ROUTE_UI_SECTION_NAME.budgetPlan]) {
    return id
      ? PROFILE_ROUTE_NAMES.budgetPlanProfileNG(id)
      : PROFILE_ROUTE_NAMES.budgetPlanCreateNG(requestId)
  }

  return id
    ? PROFILE_ROUTE_NAMES.budgetPlan(id)
    : PROFILE_ROUTE_NAMES.budgetPlanCreate(requestId)
}

export function getConnectedRequestProfilePathname({
  additionalParams,
  availableSections,
  id,
  outboundUuid,
}) {
  return availableSections[ROUTE_UI_SECTION_NAME.idwellToIdwellRequest]
    ? PROFILE_ROUTE_NAMES.connectedRequestProfileNG({
        additionalParams,
        id,
        outboundUuid,
      })
    : PROFILE_ROUTE_NAMES.connectedRequest(id, outboundUuid)
}

export function getPostProfilePathname({
  additionalParams,
  availableSections,
  id,
}) {
  return availableSections[ROUTE_UI_SECTION_NAME.post]
    ? PROFILE_ROUTE_NAMES.postProfileNG({ additionalParams, id })
    : PROFILE_ROUTE_NAMES.post(id)
}

export function getChatProfilePathname({ availableSections, uuid }) {
  return availableSections[ROUTE_UI_SECTION_NAME.chat]
    ? PROFILE_ROUTE_NAMES.chatProfileNG(uuid)
    : PROFILE_ROUTE_NAMES.chat(uuid)
}

export function getFlatProfilePathname({ availableSections, id }) {
  return availableSections?.[ROUTE_UI_SECTION_NAME.flat]
    ? PROFILE_ROUTE_NAMES.flatProfileNG({ id })
    : PROFILE_ROUTE_NAMES.flat(id)
}

export function getInhabitantProfilePathname({
  additionalParams,
  id,
  isUserProfileNGAvailable,
}) {
  return isUserProfileNGAvailable
    ? PROFILE_ROUTE_NAMES.userProfileNG({ additionalParams, id })
    : PROFILE_ROUTE_NAMES.inhabitant({ additionalParams, id })
}

export function getBuildingProfilePathname({
  additionalParams,
  availableSections,
  id,
}) {
  return availableSections?.[ROUTE_UI_SECTION_NAME.buildingDetail]
    ? PROFILE_ROUTE_NAMES.buildingProfileNG({ additionalParams, id })
    : PROFILE_ROUTE_NAMES.building(id)
}

export function getProviderProfilePathname({
  additionalParams,
  id,
  isProviderProfileNGAvailable,
}) {
  return isProviderProfileNGAvailable
    ? PROFILE_ROUTE_NAMES.providerProfileNG({ additionalParams, id })
    : PROFILE_ROUTE_NAMES.provider({ additionalParams, id })
}

export function getBuildingGroupProfilePathname({
  additionalParams,
  id,
  isBuildingGroupProfileNGAvailable,
}) {
  return isBuildingGroupProfileNGAvailable
    ? PROFILE_ROUTE_NAMES.buildingGroupProfileNG({ additionalParams, id })
    : PROFILE_ROUTE_NAMES.buildingGroup(id)
}

export function getCompanySettingsPathname({
  additionalParams = EMPTY_STRING,
  availableSections,
}) {
  const pathname = `/${ROUTE_PATH_LIST.settings}/${ROUTE_PATH_LIST_SETTINGS.company}`

  return availableSections?.[ROUTE_UI_SECTION_NAME.settingsCompany]
    ? `${pathname}${additionalParams}`
    : getWebappLinkPrefix(pathname)
}

export function getCompanySettingsSectionLinkParams(sectionId) {
  return `?${TAB_PARAM_KEY}=${COMPANY_SETTINGS_SECTION_TAB_IDS[sectionId]}${COMPANY_SETTINGS_SECTION_HASH_PREFIX}${sectionId}`
}

export function getConfigurationSettingsLinkProps({ availableSections, tab }) {
  const pathname = `/${ROUTE_PATH_LIST.settings}/${ROUTE_PATH_LIST_SETTINGS.configuration}`
  const isAvailable =
    availableSections?.[ROUTE_UI_SECTION_NAME.settingsConfiguration]

  return {
    reloadDocument: !isAvailable,
    to: isAvailable
      ? `${pathname}?${TAB_PARAM_KEY}=${tab}`
      : getWebappLinkPrefix(`${pathname}/${tab}`),
  }
}

export function getHtmlTelLink(value) {
  return `${HTML_LINK_PROTOCOL_TEL}${value}`
}

export function getHtmlEmailLink(value) {
  return `${HTML_LINK_PROTOCOL_MAILTO}${value}`
}
